import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, RotateCw, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { attachmentsApi, type FormAttachment } from '@/api/attachmentsApi'
import { ATTACHMENT_MAX_BYTES, IMAGE_FIELD_ACCEPTED_MIME } from '@/types/cgt'
import { formatBytes } from './AttachmentPreview'
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog'
import type { PreviewImage } from './ImageField'

interface Props {
  submissionId: string
  /** The cell's composite key (`rowId__colId`) — what the picture is stored against. */
  cellKey: string
  /** Names the cell in tooltips and errors, e.g. "Picture — 50 ML SYRINGE". */
  caption: string
  attachment?: FormAttachment
  locked: boolean
  busy: boolean
  error?: string
  onUpload: (file: File) => void
  onRemove: () => void
  preview?: boolean
  previewImage?: PreviewImage | null
  onPreviewImageChange?: (image: PreviewImage | null) => void
}

/**
 * An image cell in a table-layout form: a thumbnail in the row, the way a parts
 * list carries a picture of the part beside its SKU.
 *
 * ## Why it is not `ImageField` with smaller classes
 *
 * `ImageField` lays a field out — a label, a 112×80 box, a column of Replace and
 * Remove buttons beside it. Put that in a `<td>` and every row is three lines
 * tall, which is precisely the table the reference is not. Here the cell IS the
 * control: the thumbnail fills the cell and replacing is a click on it, with a
 * single unobtrusive × for removal. The label lives in the column header, where
 * a table already puts it, so nothing is repeated per row.
 *
 * ## The cell key is the field id
 *
 * Uploads go against `rowId__colId`, the same composite key the cell's value
 * would use in `FormSubmission.values`, so a picture rides the ordinary
 * attachment pipeline with no new storage and no new addressing — one
 * `FormAttachment` row per cell, replace-by-upsert, audited. The backend
 * resolves that key back to its column to check the cell really is an image one
 * (`findCellByKey`), since a composite key is on no form's field list.
 */
export function TableImageCell({
  submissionId,
  cellKey,
  caption,
  attachment,
  locked,
  busy,
  error,
  onUpload,
  onRemove,
  preview = false,
  previewImage = null,
  onPreviewImageChange,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)

  const stored = attachment ?? previewImage
  const message = error ?? localError

  const pick = (file: File | undefined) => {
    if (!file) return
    setLocalError(null)
    if (!IMAGE_FIELD_ACCEPTED_MIME.includes(file.type)) {
      setLocalError('JPEG or PNG only.')
      return
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setLocalError(`Too large — the limit is ${formatBytes(ATTACHMENT_MAX_BYTES)}.`)
      return
    }
    if (preview) {
      onPreviewImageChange?.({
        fileName: file.name,
        sizeBytes: file.size,
        objectUrl: URL.createObjectURL(file),
      })
      return
    }
    onUpload(file)
  }

  return (
    <div className='flex flex-col items-start gap-0.5'>
      <div className='group/cell relative'>
        {stored ? (
          <CellThumb
            submissionId={submissionId}
            cellKey={cellKey}
            fileName={attachment?.fileName ?? previewImage!.fileName}
            caption={caption}
            storageKey={attachment?.storageKey}
            previewUrl={previewImage?.objectUrl}
            onOpen={() => setViewerOpen(true)}
          />
        ) : (
          <button
            type='button'
            disabled={locked || busy}
            title={locked ? caption : `Add a picture — ${caption}`}
            onClick={() => inputRef.current?.click()}
            className={cn(
              'flex h-14 w-20 items-center justify-center rounded border border-dashed text-muted-foreground transition-colors',
              locked || busy
                ? 'cursor-not-allowed opacity-50'
                : 'hover:border-primary/50 hover:bg-muted/40 hover:text-foreground',
            )}
          >
            {busy ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <ImagePlus className='h-4 w-4' />
            )}
          </button>
        )}

        {/* Only on hover, and only when there is something to clear: a bin per
            row would out-shout the pictures the table exists to show. */}
        {stored && !locked && (
          <button
            type='button'
            disabled={busy}
            title={`Remove — ${caption}`}
            aria-label={`Remove image — ${caption}`}
            onClick={() => {
              setLocalError(null)
              if (preview) onPreviewImageChange?.(null)
              else onRemove()
            }}
            className='absolute -right-1.5 -top-1.5 hidden rounded-full border bg-background p-0.5 text-muted-foreground shadow-sm hover:text-destructive group-hover/cell:block'
          >
            <X className='h-3 w-3' />
          </button>
        )}
      </div>

      {message && <span className='text-[10px] leading-tight text-destructive'>{message}</span>}

      <input
        ref={inputRef}
        type='file'
        accept={IMAGE_FIELD_ACCEPTED_MIME.join(',')}
        className='hidden'
        onChange={e => {
          pick(e.target.files?.[0])
          e.target.value = ''
        }}
      />

      {viewerOpen && attachment && (
        <AttachmentPreviewDialog
          submissionId={submissionId}
          fieldId={cellKey}
          fileName={attachment.fileName}
          sizeBytes={attachment.sizeBytes}
          open={viewerOpen}
          onOpenChange={setViewerOpen}
        />
      )}
    </div>
  )
}

/** The picture itself, fetching its own download URL the way AttachmentThumb does. */
function CellThumb({
  submissionId,
  cellKey,
  fileName,
  caption,
  storageKey,
  previewUrl,
  onOpen,
}: {
  submissionId: string
  cellKey: string
  fileName: string
  caption: string
  storageKey?: string
  previewUrl?: string
  onOpen: () => void
}) {
  const [url, setUrl] = useState<string | null>(previewUrl ?? null)
  const [failed, setFailed] = useState(false)
  const [retryTick, setRetryTick] = useState(0)

  useEffect(() => {
    if (previewUrl) {
      setUrl(previewUrl)
      return
    }
    let cancelled = false
    setUrl(null)
    setFailed(false)
    attachmentsApi
      .getDownloadUrl(submissionId, cellKey)
      .then(res => {
        if (!cancelled) setUrl(res.url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [submissionId, cellKey, storageKey, previewUrl, retryTick])

  if (failed) {
    return (
      <button
        type='button'
        title={`Couldn't load ${fileName} — click to retry`}
        onClick={() => setRetryTick(t => t + 1)}
        className='flex h-14 w-20 items-center justify-center rounded border border-dashed border-destructive/40 text-destructive hover:bg-destructive/5'
      >
        <RotateCw className='h-3.5 w-3.5' />
      </button>
    )
  }

  return (
    <button
      type='button'
      onClick={onOpen}
      title={`${fileName} — ${caption}. Click to view full size.`}
      className='h-14 w-20 overflow-hidden rounded border bg-muted/40 transition-colors hover:border-primary/50'
    >
      {url ? (
        <img src={url} alt={`${caption} — ${fileName}`} className='h-full w-full object-cover' />
      ) : (
        <span className='flex h-full w-full items-center justify-center'>
          <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
        </span>
      )}
    </button>
  )
}
